'use client'

import { useCallback } from 'react'
import { useAuth } from '@clerk/nextjs'
import posthog from 'posthog-js'
import { clearLocalStorage, LOCAL_STORAGE_KEYS } from '@/setup/local-storage'

type UseSignOutOptions = {
    /**
     * URL to redirect to after sign out. Defaults to Clerk's configured redirect.
     */
    redirectUrl?: string
    /**
     * Callback to run after successful sign out (e.g., redirect)
     */
    onSuccess?: () => void

    beforeSignOut?: () => void
    /**
     * Callback to run on sign out error
     */
    onError?: (error: unknown) => void
}

/**
 * Hook for signing out with cleanup of local state and cache.
 *
 * Performs the following cleanup:
 * - Signs out via Clerk
 * - Clears React Query cache
 * - Clears relevant localStorage items (onboarding data, sidebar state, etc.)
 *
 * @example
 * ```tsx
 * const { signOut, isSigningOut } = useSignOut({
 *     onSuccess: () => router.push('/auth'),
 * })
 *
 * <Button onClick={signOut} disabled={isSigningOut}>
 *     Sign Out
 * </Button>
 * ```
 */
export function useSignOut(options: UseSignOutOptions = {}) {
    const { redirectUrl, onSuccess, onError, beforeSignOut } = options
    const { signOut: clerkSignOut } = useAuth()

    const signOut = useCallback(async () => {
        try {
            beforeSignOut?.()

            clearLocalStorage({ except: (key) => key === LOCAL_STORAGE_KEYS.Auth.LastUsedMethod })

            posthog.reset()

            await clerkSignOut({ redirectUrl })

            onSuccess?.()
        } catch (error) {
            console.error('Error during sign out:', error)
            onError?.(error)
        }
    }, [beforeSignOut, clerkSignOut, redirectUrl, onSuccess, onError])

    return { signOut }
}
